import type { PlacesLibrary } from "./types";

declare global {
  interface Window {
    google?: {
      maps?: {
        places?: PlacesLibrary;
      };
    };
  }
}

const GOOGLE_MAPS_API_KEY =
  window?.env?.VITE_PLACES_KEY || import.meta.env.VITE_PLACES_KEY;

const SCRIPT_ID = "google-maps-places";
const SCRIPT_SRC = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
  GOOGLE_MAPS_API_KEY ?? ""
)}&libraries=places&v=weekly`;

/**
 * Memoised so every picker on the page shares one <script> and one in-flight
 * load. The previous two implementations each injected their own tag, which
 * triggered Google's "included the JavaScript API multiple times" error on any
 * screen that rendered both.
 *
 * Deliberately not using loading=async + importLibrary(): without a callback,
 * google.maps.places is not guaranteed to be populated by the time the script's
 * load event fires. The synchronous bootstrap below is what the app already
 * relied on. Swapping to importLibrary() later is a change to this file alone.
 */
let libraryPromise: Promise<PlacesLibrary> | null = null;

export function loadPlacesLibrary(): Promise<PlacesLibrary> {
  if (libraryPromise) {
    return libraryPromise;
  }

  libraryPromise = new Promise<PlacesLibrary>((resolve, reject) => {
    const alreadyLoaded = window.google?.maps?.places;

    if (alreadyLoaded) {
      resolve(alreadyLoaded);
      return;
    }

    if (!GOOGLE_MAPS_API_KEY) {
      reject(new Error("VITE_PLACES_KEY is not configured"));
      return;
    }

    const handleLoad = () => {
      const places = window.google?.maps?.places;

      if (places) {
        resolve(places);
      } else {
        reject(new Error("Google Maps loaded without the places library"));
      }
    };

    // Drop the dead tag, otherwise a retry attaches listeners to a script that
    // will never fire load again and the promise hangs forever.
    const handleError = () => {
      document.getElementById(SCRIPT_ID)?.remove();
      reject(new Error("Failed to load the Google Maps JavaScript API"));
    };

    // A tag can already be in the DOM from a previous, since-unmounted picker
    // whose load had not resolved yet.
    const existingScript = document.getElementById(SCRIPT_ID);

    if (existingScript) {
      existingScript.addEventListener("load", handleLoad);
      existingScript.addEventListener("error", handleError);
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = SCRIPT_SRC;
    script.addEventListener("load", handleLoad);
    script.addEventListener("error", handleError);
    document.head.appendChild(script);
  });

  // Never cache a rejection: one offline blip would otherwise break every
  // location field for the rest of the session.
  libraryPromise.catch(() => {
    libraryPromise = null;
  });

  return libraryPromise;
}
